// DatabaseSettingsAutomationsTab.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  getAutomationRuleDescription,
  getAutomationRuleTitle,
} from 'collection/automations/utils/automationRuleUtils';
import { isSystemTypeAutomation } from 'services/automations/TypeAutomationService';

// CSS
import './DatabaseSettingsAutomationsTab.css';

// Components
import DatabaseSettingsAutomationRow from './DatabaseSettingsAutomationRow';
import DatabaseSettingsAddButton from 'collection/settings/addbutton/DatabaseSettingsAddButton';

const buildAutomationSummary = (rule, fieldOptions) => {
  const fieldLabelByKey = new Map((Array.isArray(fieldOptions) ? fieldOptions : []).map((field) => [field.key, field.label]));
  const triggerFieldKey = String(rule?.trigger?.fieldKey || '').trim();
  const triggerLabel = fieldLabelByKey.get(triggerFieldKey) || 'a field';
  const actionCount = Array.isArray(rule?.actions) ? rule.actions.length : 0;
  return `When ${triggerLabel} changes${actionCount ? ` • ${actionCount} action${actionCount === 1 ? '' : 's'}` : ''}`;
};

/**
 * DatabaseSettingsAutomationsTab
 *
 * Renders the automations list tab inside database settings.
 *
 * @param {object[]} rules - Saved automation rules for the active database.
 * @param {array} fieldOptions - Available fields for summary rendering.
 * @param {function} onAddAutomation - Opens the create-automation modal.
 * @param {function} onEditAutomation - Opens the edit-automation modal.
 * @param {boolean} [lockSystemAutomations=false] - When true, system rows are read-only.
 *
 * @returns {ReactNode} The rendered automations tab.
 */
const DatabaseSettingsAutomationsTab = React.memo(({
  rules = [],
  fieldOptions = [],
  onAddAutomation,
  onEditAutomation,
  lockSystemAutomations = false,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- MEMOS ----- //

  const automationRows = useMemo(() => {
    return (Array.isArray(rules) ? rules : []).map((rule) => ({
      key: String(rule?.key || '').trim(),
      label: getAutomationRuleTitle(rule),
      note: getAutomationRuleDescription(rule) || buildAutomationSummary(rule, fieldOptions),
      rule,
      locked: lockSystemAutomations && isSystemTypeAutomation(rule),
    })).filter((row) => row.key);
  }, [fieldOptions, lockSystemAutomations, rules]);

  // ----- RENDER ----- //

  return (
    <div className="database-modal-views">

      {/* AUTOMATION ACTIONS */}
      <div className="database-modal-automation-actions">
        <DatabaseSettingsAddButton
          title="Add Automation"
          onClick={() => onAddAutomation?.()}
        />
      </div>

      {automationRows.length > 0 ? (
        <div className="database-modal-view-list">
          {automationRows.map((row) => (
            <DatabaseSettingsAutomationRow
              key={row.key}
              label={row.label}
              note={row.note}
              locked={row.locked}
              badge={row.locked ? 'System' : ''}
              onClick={() => {
                if (row.locked) return;
                onEditAutomation?.(row.rule);
              }}
              onSettingsClick={() => {
                if (row.locked) return;
                onEditAutomation?.(row.rule);
              }}
            />
          ))}
        </div>
      ) : (
        <div
          className="database-modal-empty-state"
          style={{
            color: theme.foregroundMuted,
          }}
        >
          No automations yet.
        </div>
      )}
    </div>
  );
});

// Dev Tools
DatabaseSettingsAutomationsTab.displayName = 'DatabaseSettingsAutomationsTab';

export default DatabaseSettingsAutomationsTab;
